import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import Heroicon from "../Heroicon";
import Switch from "../Switch";
import PodcastLocked from "./PodcastLocked";
import PodcastResults from "./PodcastResults";
import PodcastSummary from "./PodcastSummary";
import Errors from "./Errors";

function SearchPodcastToImport() {
  const axios = window.axios;
  const [searchQuery, setSearchQuery] = useState("");
  const [feedStatus, setFeedStatus] = useState(null);
  const [podcastResults, setPodcastResults] = useState([]);
  const [selectedPodcast, setSelectedPodcast] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const errors = selectedPodcast && selectedPodcast.errors;

  async function readFeed(feed) {
    let result;
    await axios
      .get("/imports/feed-reader/by-url", {
        params: {
          feed: feed,
        },
      })
      .then((response) => {
        result = {
          status: "valid",
          ...response.data,
        };
      })
      .catch((error) => {
        const errorData = error.response.data.errors;
        result = {
          status: "invalid",
          errors: errorData
            ? errorData.parsed_rss_feed
            : ["Error loading RSS feed, try again later."],
        };
        console.error(error);
      });
    return result;
  }

  async function validateFeed() {
    const result = await readFeed(searchQuery);
    setSelectedPodcast(result);
    setFeedStatus(result.status);
  }

  async function searchPodcast() {
    await axios
      .get("/search/podcasts/by-title", {
        params: {
          podcast: searchQuery,
        },
      })
      .then((response) => {
        response.data.feeds.length === 0
          ? setPodcastResults("empty")
          : setPodcastResults(response.data.feeds);
      })
      .catch((error) => {
        console.error(error);
      });
    setFeedStatus(null);
  }

  function selectPodcast(feed, time = 0) {
    setSelectedPodcast({
      status: "validating",
      url: feed,
    });
    const timer = setTimeout(async () => {
      const result = await readFeed(feed);

      if (result.status === "valid") {
        setSelectedPodcast(result);
      } else {
        setSelectedPodcast({
          status: "invalid",
          url: feed,
          errors: result.errors,
        });
      }
    }, time);
    return () => clearTimeout(timer);
  }

  function resetSearch() {
    if (podcastResults) setPodcastResults([]);
    if (selectedPodcast) setSelectedPodcast(null);
  }

  useEffect(() => {
    resetSearch();

    if (searchQuery !== "") {
      setFeedStatus("searching");
      const timer = setTimeout(() => {
        searchQuery.includes("http") ? validateFeed() : searchPodcast();
      }, 500);

      return () => {
        clearTimeout(timer);
      };
    } else {
      setFeedStatus(null);
    }
  }, [searchQuery]);

  function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    axios
      .post("/imports/submit", {
        feed: selectedPodcast.url,
      })
      .then((response) => {
        applyTags("import_feed_submitted");
        window.location.replace(`/imports/${response.data.id}/success`);
      })
      .catch((error) => {
        console.error(error);
        const errorData = error.response.data.errors;
        setSelectedPodcast({
          ...selectedPodcast,
          errors: errorData
            ? errorData.parsed_rss_feed
            : ["Something went wrong, try again later."],
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  }

  return (
    <>
      {feedStatus !== "ready" ? (
        <>
          <h1 className="mt-6 text-2xl md:mt-0text-gray-800 md:text-3xl md:text-center">
            Search for a Podcast to import
          </h1>
          <section className="p-5 mt-6 mb-20 bg-white rounded-lg shadow md:p-7">
            <label htmlFor="search-podcast" className="sr-only">
              Search Podcast
            </label>
            <input
              type="text"
              className={`input text-sm sm:text-base ${
                feedStatus === "invalid"
                  ? "input--danger bg-red-50"
                  : "bg-gray-100"
              }`}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Type a podcast name..."
            />
            <Switch exp={feedStatus}>
              <p case={null} className="field-help-text">
                Or, you can also paste in your RSS feed URL if you have it on
                hand.
              </p>
              <p
                case="searching"
                className="flex items-center gap-2 text-blue-600 field-help-text"
              >
                <Heroicon icon="o-arrow-path" style="w-5 h-5 animate-spin" />
                Searching
              </p>
              <p
                case="valid"
                className="flex items-center gap-2 text-green-600 field-help-text"
              >
                <Heroicon icon="o-check-circle" style="w-5 h-5" />
                Valid RSS feed URL!
              </p>
              <div
                case="invalid"
                className="flex items-center gap-2 field-help-text field-help-text--danger"
              >
                <Heroicon icon="o-exclamation-circle" style="w-5 h-5" />
                {errors && <Errors errors={selectedPodcast.errors} />}
              </div>
            </Switch>
            {Array.isArray(podcastResults) && podcastResults.length > 0 && (
              <>
                <hr className="my-4" aria-hidden="true" />
                <PodcastResults
                  results={podcastResults}
                  selectedPodcast={selectedPodcast}
                  selectPodcast={selectPodcast}
                />
                <hr className="my-4" aria-hidden="true" />
              </>
            )}
            {podcastResults === "empty" && (
              <p className="flex items-center justify-center gap-2 mt-5 text-sm badge badge--warn w-max">
                <Heroicon icon="o-exclamation-circle" style="w-5 h-5" />
                No podcast results found.
              </p>
            )}
            {selectedPodcast && selectedPodcast.status === "valid" ? (
              <button
                className="w-full mt-4 text-sm rounded-lg btn-solid btn-primary sm:text-base"
                onClick={() => {
                  setFeedStatus("ready");
                  if (searchQuery.includes("https://")) {
                    applyTags("import_via_feed");
                  } else {
                    applyTags("import_via_search");
                  }
                  if (selectedPodcast.locked) {
                    applyTags("import_locked");
                  }
                }}
              >
                Next
              </button>
            ) : (
              <button
                disabled
                className="w-full mt-4 text-sm rounded-lg cursor-not-allowed btn-outlined btn-primary sm:text-base"
              >
                Next
              </button>
            )}
          </section>
        </>
      ) : (
        <Switch exp={selectedPodcast.locked}>
          <PodcastLocked
            case={1}
            feed={selectedPodcast.url}
            readFeed={readFeed}
            updatePodcast={setSelectedPodcast}
          />
          <div case={0}>
            <h1 className="mt-6 text-2xl text-gray-800 md:mt-0 md:text-3xl md:text-center">
              Before we start
            </h1>
            <p className="mt-4 text-sm text-gray-700 sm:text-base md:text-center">
              Take a look at the below information we found in your RSS feed,
              once you confirm we can proceed to start your import process.
            </p>
            {errors && (
              <div className="flex items-center gap-3 mt-4 alert alert--danger">
                <Heroicon icon="o-exclamation-circle" style="w-5 h-5" />
                <Errors errors={selectedPodcast.errors} />
              </div>
            )}
            <form
              method="POST"
              className="mt-6 mb-20 bg-white rounded-lg shadow"
              onSubmit={handleSubmit}
            >
              <PodcastSummary selectedPodcast={selectedPodcast} />
              <label
                htmlFor="confirm"
                className="flex items-center gap-3 px-5 py-4 text-sm text-purple-800 bg-purple-200 md:px-7"
              >
                <input
                  type="checkbox"
                  className="rounded hue-rotate-[40deg]"
                  name="confirm"
                  id="confirm"
                  required="required"
                />
                This is my podcast and/or I have the right to import it.
              </label>
              <div className="p-5 pt-3 md:p-7">
                {selectedPodcast.errors || submitting ? (
                  <button
                    disabled
                    className="flex items-center justify-center w-full gap-2 text-sm rounded-lg cursor-not-allowed btn-outlined btn-primary sm:text-base"
                  >
                    Next
                    {submitting && (
                      <Heroicon
                        icon="o-arrow-path"
                        style="w-5 h-5 animate-spin"
                      />
                    )}
                  </button>
                ) : (
                  <button className="w-full text-sm rounded-lg btn-solid btn-primary sm:text-base">
                    Next
                  </button>
                )}
              </div>
            </form>
          </div>
        </Switch>
      )}
      <div
        className="absolute left-0 right-0 grid place-items-center bottom-10"
        aria-hidden="true"
        role="presentation"
      >
        <div className="flex gap-1.5 items-center">
          <div className="w-2 h-2 bg-purple-300 rounded-full"></div>
          <div
            className={`w-2 h-2 rounded-full ${
              feedStatus !== "ready" ? "bg-purple-600" : "bg-purple-300"
            }`}
          ></div>
          <div
            className={`w-2 h-2 rounded-full ${
              feedStatus === "ready" ? "bg-purple-600" : "bg-purple-300"
            }`}
          ></div>
          <div className="w-2 h-2 bg-purple-300 rounded-full"></div>
        </div>
      </div>
    </>
  );
}

const initSearchPodcastToImport = function (selector) {
  const elements = document.querySelectorAll(selector);
  if (elements.length > 0) {
    elements.forEach((element) => {
      const root = createRoot(element);
      root.render(<SearchPodcastToImport />);
    });
  }
};

export { SearchPodcastToImport as default, initSearchPodcastToImport };
